import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Grid, Stack, Divider, Alert } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAuth } from '../context/AuthContext';

// Modals & Print
import AddPaymentModal from '../components/modals/AddPaymentModal';
import TechnicalServiceForm from '../components/print/TechnicalServiceForm';

// New Modular Components & Hooks
import GlassCard from '../ui/glass/components/GlassCard';
import { Loading } from '../components/common/Loading';
import {
  useServiceDetail,
  ServiceDetailInfo,
  ServicePartsTable,
  ServiceFinancials
} from '../modules/serviceRecords';

const ServisKaydiDetay = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  // Custom Hook
  const {
    serviceRecord,
    payments,
    customer,
    loading,
    kalanSure,
    handleAddPayment,
    handleDeletePayment
  } = useServiceDetail(id);

  if (loading || !currentUser) {
    return <Loading message="Servis kaydı yükleniyor..." />;
  }

  if (!serviceRecord) {
    return <Typography variant="h5">Servis Kaydı Bulunamadı.</Typography>;
  }

  const kayitTarihi = serviceRecord.olusturmaTarihi?.toDate().toLocaleDateString('tr-TR');

  return (
    <Box className="printable-area" sx={{ p: { xs: 1, md: 2 } }}>
      <style type="text/css" media="print">
        {`
                  .printable-area > *:not(.technical-service-form-wrapper) {
                    display: none !important;
                  }
                `}
      </style>

      <AddPaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSave={handleAddPayment}
        kalanTutar={serviceRecord.kalanTutar ?? 0}
      />

      {/* Header Section */}
      <GlassCard className="no-print" sx={{ mb: 3, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Servis Kaydı: {serviceRecord.serviceNo ? `#${String(serviceRecord.serviceNo).padStart(6, '0')}` : `#${serviceRecord.cihazSeriNumarasi}`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Kayıt Tarihi: {kayitTarihi}
            </Typography>
          </Box>
          <Box>
            {currentUser && (currentUser.role === 'Yönetici' || currentUser.role === 'Ofis Personeli') && (
              <Button
                variant="outlined"
                color="info"
                startIcon={<EditIcon />}
                sx={{ mr: 1 }}
                onClick={() => navigate(`/servis-kaydi/duzenle/${id}`)}
              >
                Düzenle
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
            >
              Yazdır
            </Button>
          </Box>
        </Box>
      </GlassCard>

      <Grid container spacing={3}>
        <Grid item xs={12} md={currentUser?.role === 'Servis Teknisyeni' ? 12 : 8}>
          <Stack spacing={3}>

            {/* Müşteri ve Cihaz Bilgileri */}
            <GlassCard sx={{ p: 3 }}>
              <ServiceDetailInfo serviceRecord={serviceRecord} />
            </GlassCard>

            {/* Servis Notları ve Genel Bilgiler */}
            <GlassCard sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>Servis Detayları</Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Servis Numarası</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {serviceRecord.serviceNo ? `#${String(serviceRecord.serviceNo).padStart(6, '0')}` : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Kayıt Tipi</Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {serviceRecord.kayitTipi || 'Servis'}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Arıza Tanımı / Müşteri Talebi</Typography>
              <Typography paragraph sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'primary.main', fontStyle: 'italic', bgcolor: 'rgba(0,0,0,0.02)', py: 1, borderRadius: '0 4px 4px 0' }}>
                {serviceRecord.arizaTanimi || 'Belirtilmemiş'}
              </Typography>

              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Yapılan İşlemler</Typography>
              <Typography paragraph sx={{ pl: 1 }}>
                {serviceRecord.yapilanIslemler || 'Belirtilmemiş'}
              </Typography>
            </GlassCard>

            {/* Kullanılan Malzemeler */}
            <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
              <Box sx={{ p: 2, pb: 0 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>Malzemeler & Hizmetler</Typography>
              </Box>
              <ServicePartsTable serviceRecord={serviceRecord} />
            </GlassCard>

            {/* Tarihler ve Notlar */}
            <GlassCard sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>Notlar ve Tarihler</Typography>
              <Divider sx={{ mb: 2 }} />

              {kalanSure && (
                <Alert
                  severity={kalanSure === "Teslim tarihi geçti!" ? "error" : "info"}
                  icon={<AccessTimeIcon fontSize="inherit" />}
                  sx={{ mb: 2, borderRadius: 2 }}
                  className="no-print"
                >
                  <Typography>Teslime Kalan Süre: <strong>{kalanSure}</strong></Typography>
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Oluşturulma Tarihi</Typography>
                  <Typography variant="body1">{kayitTarihi}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Planlanan Teslim Tarihi</Typography>
                  <Typography variant="body1">{serviceRecord.teslimTarihi?.toDate().toLocaleDateString('tr-TR')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Genel Notlar</Typography>
                  <Typography variant="body1" sx={{ bgcolor: 'rgba(0,0,0,0.02)', p: 1, borderRadius: 1, mt: 0.5 }}>
                    {serviceRecord.servisNotlari || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </GlassCard>
          </Stack>
        </Grid>

        {/* Yan Panel: Finansal Bilgiler */}
        {currentUser?.role !== 'Servis Teknisyeni' && (
          <Grid item xs={12} md={4}>
            {/* ServiceFinancials component might need its own GlassCard wrapper or it implements one. 
                Assuming it's a standalone component, we can wrap it if it renders a Paper/Card.
                Let's wrap it in a Box for now, or if it's styled, leave it. 
                The plan said: "ServiceFinancials is a sidebar component. I will ensure it sits correctly".
                I'll assume it needs a container if it was previously naked.
                In original file it was just `<ServiceFinancials ... />`.
                Most likely it renders a Card. If I wrap it in GlassCard, I might double card.
                I will wrap it for safety as "GlassCard" is the theme.
            */}
            <GlassCard sx={{ p: 2 }}>
              <ServiceFinancials
                serviceRecord={serviceRecord}
                payments={payments}
                onAddPaymentClick={() => setIsPaymentModalOpen(true)}
                onDeletePayment={handleDeletePayment}
              />
            </GlassCard>
          </Grid>
        )}
      </Grid>

      <div className="technical-service-form-wrapper">
        <TechnicalServiceForm data={{ serviceRecord, customer, payments }} />
      </div>
    </Box>
  );
};

export default ServisKaydiDetay;
